import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Ticket,
  MessageSquare,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertCircle,
  Mail,
  ShoppingBag,
  Bot,
  BookOpen,
  Zap,
  CheckCircle2,
  Circle,
  ArrowRight,
  Sparkles,
  FileText,
  Link2,
} from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken, useAuth } from '@/hooks/useAuth'
import { API_BASE } from '@/services/api'

interface Stats {
  totalConversations: number
  totalMessages: number
  avgResponseTime: number
  resolutionRate: number
  conversationsChange: number
  messagesChange: number
}

interface RecentConversation {
  session_id: string
  customer_email?: string
  last_message?: string
  category: string
  created_at: string
  status: string
}

interface IntegrationStatus {
  gmail: 'connected' | 'disconnected' | 'loading'
  openai: 'connected' | 'disconnected' | 'loading'
  shopify: 'connected' | 'disconnected' | 'loading'
}

interface AutoDraftProgress {
  status: string
  total_threads: number
  classified: number
  drafts_generated: number
  message: string
  updated_at: string
}

// Module-level cache to persist data across navigation
let _cachedStats: Stats | null = null
let _cachedConversations: RecentConversation[] = []
let _cachedIntegrations: IntegrationStatus | null = null
let _cachedAutoDraft: AutoDraftProgress | null = null

export default function DashboardPage() {
  const { tenantId, isLoading: tenantLoading } = useTenant()
  const { user } = useAuth()
  const [stats, setStats] = useState<Stats | null>(_cachedStats)
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>(_cachedConversations)
  const [isLoading, setIsLoading] = useState(!_cachedStats)
  const [error, setError] = useState<string | null>(null)
  const [integrations, setIntegrations] = useState<IntegrationStatus>(
    _cachedIntegrations || { gmail: 'loading', openai: 'loading', shopify: 'loading' }
  )
  const [autoDraft, setAutoDraft] = useState<AutoDraftProgress | null>(_cachedAutoDraft)
  const [agentCount, setAgentCount] = useState<number | null>(null)
  const [kbDocCount, setKbDocCount] = useState<number | null>(null)

  useEffect(() => {
    if (tenantId && !tenantLoading) {
      loadDashboardData()
    }
  }, [tenantId, tenantLoading])

  const loadDashboardData = async () => {
    if (!tenantId) {
      setError('No tenant ID available')
      setIsLoading(false)
      return
    }

    if (!_cachedStats) {
      setIsLoading(true)
    }
    setError(null)

    try {
      const token = await getAuthToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId,
      }
      if (token && token !== 'demo-token') {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Load all data in parallel
      const [analyticsRes, conversationsRes, integrationsRes, autoDraftRes, agentsRes, kbRes] = await Promise.allSettled([
        fetch(`${API_BASE}/v1/analytics?period=week`, { headers }),
        fetch(`${API_BASE}/v1/conversations?limit=5`, { headers }),
        fetch(`${API_BASE}/v1/integrations`, { headers }),
        fetch(`${API_BASE}/v1/router/auto-draft-progress`, { headers }),
        fetch(`${API_BASE}/v1/agents`, { headers }),
        fetch(`${API_BASE}/v1/knowledge-base/stats`, { headers }),
      ])

      // Analytics
      if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
        const data = await analyticsRes.value.json()
        const newStats = {
          totalConversations: data.total_conversations || 0,
          totalMessages: data.total_messages || 0,
          avgResponseTime: (data.avg_response_time_ms || 0) / 1000,
          resolutionRate: data.resolution_rate || 0,
          conversationsChange: 0,
          messagesChange: 0,
        }
        setStats(newStats)
        _cachedStats = newStats
      }

      // Conversations
      if (conversationsRes.status === 'fulfilled' && conversationsRes.value.ok) {
        const data = await conversationsRes.value.json()
        const convos = data.conversations || []
        setRecentConversations(convos)
        _cachedConversations = convos
      }

      // Integrations status (same endpoint IntegrationsPage uses)
      if (integrationsRes.status === 'fulfilled' && integrationsRes.value.ok) {
        const data = await integrationsRes.value.json()
        const connectedIds: string[] = data.integrations || []
        const intStatus: IntegrationStatus = {
          gmail: connectedIds.includes('gmail') ? 'connected' : 'disconnected',
          openai: connectedIds.includes('openai') ? 'connected' : 'disconnected',
          shopify: connectedIds.includes('shopify') ? 'connected' : 'disconnected',
        }
        setIntegrations(intStatus)
        _cachedIntegrations = intStatus
      } else {
        setIntegrations({ gmail: 'disconnected', openai: 'disconnected', shopify: 'disconnected' })
      }

      // Auto-draft progress
      if (autoDraftRes.status === 'fulfilled' && autoDraftRes.value.ok) {
        const data = await autoDraftRes.value.json()
        setAutoDraft(data)
        _cachedAutoDraft = data
      }

      // Agents count
      if (agentsRes.status === 'fulfilled' && agentsRes.value.ok) {
        const data = await agentsRes.value.json()
        setAgentCount((data.agents || []).length)
      }

      // KB docs count
      if (kbRes.status === 'fulfilled' && kbRes.value.ok) {
        const data = await kbRes.value.json()
        setKbDocCount(data.total_documents || 0)
      }

    } catch (err: any) {
      console.error('Error loading dashboard:', err)
      setError('Failed to load dashboard data')
      if (!_cachedStats) {
        setStats({
          totalConversations: 0,
          totalMessages: 0,
          avgResponseTime: 0,
          resolutionRate: 0,
          conversationsChange: 0,
          messagesChange: 0,
        })
      }
      setIntegrations({ gmail: 'disconnected', openai: 'disconnected', shopify: 'disconnected' })
    } finally {
      setIsLoading(false)
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      ShippingDelivery: 'bg-blue-50 text-blue-700 ring-blue-600/10',
      ReturnsCancellationsExchanges: 'bg-orange-50 text-orange-700 ring-orange-600/10',
      PaymentBilling: 'bg-purple-50 text-purple-700 ring-purple-600/10',
      ProductInfoAvailability: 'bg-green-50 text-green-700 ring-green-600/10',
      CustomerComplaintsSatisfaction: 'bg-red-50 text-red-700 ring-red-600/10',
      AccountProfileOther: 'bg-gray-50 text-gray-700 ring-gray-600/10',
    }
    return colors[category] || 'bg-gray-50 text-gray-700 ring-gray-600/10'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-emerald-50 text-emerald-700',
      resolved: 'bg-gray-50 text-gray-600',
      escalated: 'bg-red-50 text-red-700',
    }
    return colors[status] || 'bg-gray-50 text-gray-600'
  }

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays === 1) return 'Yesterday'
    return `${diffDays}d ago`
  }

  // Setup checklist items
  const setupSteps = [
    { label: 'Connect Gmail', done: integrations.gmail === 'connected', href: '/integrations', icon: Mail },
    { label: 'Connect Shopify', done: integrations.shopify === 'connected', href: '/integrations', icon: ShoppingBag },
    { label: 'Configure AI Agents', done: (agentCount ?? 0) > 0, href: '/agents', icon: Sparkles },
    { label: 'Upload Knowledge Base docs', done: (kbDocCount ?? 0) > 0, href: '/knowledge-base', icon: FileText },
  ]
  const completedSteps = setupSteps.filter(s => s.done).length
  const allSetupComplete = completedSteps === setupSteps.length

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {getGreeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Here's an overview of your support operations.</p>
        </div>
        <button
          onClick={loadDashboardData}
          disabled={isLoading}
          className="flex items-center px-3.5 py-2 bg-white border border-gray-200 hover:border-gray-300 rounded-xl text-sm font-medium text-gray-700 transition-all disabled:opacity-50 shadow-sm hover:shadow"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-3.5 flex items-center space-x-2.5">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      {/* Getting Started Checklist */}
      {!isLoading && (
        allSetupComplete ? (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">All set! Your AI support pipeline is active.</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Emails are being processed automatically. Check your Inbox for tickets.</p>
                </div>
              </div>
              <Link
                to="/inbox"
                className="flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Open Inbox
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Getting Started</h2>
                <p className="text-xs text-gray-500 mt-0.5">Complete these steps to activate your AI support pipeline</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${(completedSteps / setupSteps.length) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-amber-700">{completedSteps}/{setupSteps.length}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {setupSteps.map((step) => (
                <Link
                  key={step.label}
                  to={step.href}
                  className={`flex items-center space-x-2.5 px-3 py-2.5 rounded-lg transition-all text-[12px] font-medium ${
                    step.done
                      ? 'bg-white/60 text-emerald-700'
                      : 'bg-white text-gray-700 hover:bg-white/80 shadow-sm'
                  }`}
                >
                  {step.done ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <step.icon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                  <span className={step.done ? 'line-through opacity-60' : ''}>{step.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: 'Open Tickets', value: stats?.totalConversations || 0, icon: Ticket, change: stats?.conversationsChange, color: 'amber' },
          { title: 'Messages', value: stats?.totalMessages || 0, icon: MessageSquare, change: stats?.messagesChange, color: 'blue' },
          { title: 'Avg Response', value: stats?.avgResponseTime ? `${stats.avgResponseTime.toFixed(1)}s` : '0s', icon: Clock, color: 'purple' },
          { title: 'Resolution Rate', value: stats?.resolutionRate ? `${stats.resolutionRate.toFixed(0)}%` : '0%', icon: TrendingUp, color: 'emerald' },
        ].map((stat) => (
          <div key={stat.title} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{stat.title}</span>
              <div className={`p-2 rounded-lg ${
                stat.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                stat.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                stat.color === 'purple' ? 'bg-purple-50 text-purple-600' :
                'bg-emerald-50 text-emerald-600'
              }`}>
                <stat.icon className="w-4 h-4" />
              </div>
            </div>
            {isLoading ? (
              <div className="h-8 flex items-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
              </div>
            ) : (
              <p className="text-2xl font-bold text-gray-900 tracking-tight">
                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </p>
            )}
            {stat.change !== undefined && stat.change !== 0 && (
              <div className="flex items-center mt-2">
                {stat.change >= 0 ? (
                  <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />
                )}
                <span className={`text-xs font-medium ml-0.5 ${stat.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {Math.abs(stat.change)}%
                </span>
                <span className="text-xs text-gray-400 ml-1.5">vs last week</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Auto-Draft Status Card */}
      {autoDraft && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2.5 rounded-lg ${
                autoDraft.status === 'running' ? 'bg-amber-50' :
                autoDraft.status === 'completed' ? 'bg-emerald-50' :
                'bg-gray-50'
              }`}>
                <Sparkles className={`w-5 h-5 ${
                  autoDraft.status === 'running' ? 'text-amber-600 animate-pulse' :
                  autoDraft.status === 'completed' ? 'text-emerald-600' :
                  'text-gray-400'
                }`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">AI Auto-Draft Worker</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {autoDraft.status === 'running' ? 'Processing emails now...' :
                   autoDraft.status === 'completed' ? 'Last run completed' :
                   'Waiting for next scheduled run'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-center">
              <div>
                <p className="text-lg font-bold text-gray-900">{autoDraft.classified}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Classified</p>
              </div>
              <div className="w-px h-8 bg-gray-100" />
              <div>
                <p className="text-lg font-bold text-gray-900">{autoDraft.drafts_generated}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Drafts</p>
              </div>
              <div className="w-px h-8 bg-gray-100" />
              <div>
                <p className="text-lg font-bold text-gray-900">{autoDraft.total_threads}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Threads</p>
              </div>
              {autoDraft.updated_at && (
                <>
                  <div className="w-px h-8 bg-gray-100" />
                  <div>
                    <p className="text-xs font-medium text-gray-600">{formatTimeAgo(autoDraft.updated_at)}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Last Run</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Activity - Takes 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Recent Activity</h2>
            <Link
              to="/inbox"
              className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center group"
            >
              View all tickets
              <ChevronRight className="w-3.5 h-3.5 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          <div className="divide-y divide-gray-50">
            {isLoading ? (
              <div className="p-10 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
              </div>
            ) : recentConversations.length > 0 ? (
              recentConversations.map((conversation) => (
                <div key={conversation.session_id} className="px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-600">
                          {(conversation.customer_email || 'A').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conversation.customer_email || `Session ${conversation.session_id?.slice(0, 8) || 'Unknown'}`}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {conversation.last_message || 'No messages'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ring-1 ring-inset ${getCategoryColor(conversation.category)}`}>
                        {(conversation.category || 'General').replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ${getStatusColor(conversation.status)}`}>
                        {conversation.status || 'active'}
                      </span>
                      <span className="text-xs text-gray-400 ml-1 tabular-nums">
                        {formatTimeAgo(conversation.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-10 text-center">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-amber-500" />
                </div>
                <p className="text-sm font-medium text-gray-700">No activity yet</p>
                <p className="text-xs text-gray-500 mt-1.5 max-w-xs mx-auto">
                  Connect your Gmail account and enable auto-classification to start receiving tickets automatically.
                </p>
                <Link
                  to="/integrations"
                  className="inline-flex items-center mt-3 text-xs font-medium text-amber-600 hover:text-amber-700"
                >
                  Go to Integrations
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* System Status */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">System Status</h2>
            </div>
            <div className="p-4 space-y-1">
              {[
                { key: 'gmail' as const, name: 'Gmail', icon: Mail, description: 'Email automation', href: '/integrations' },
                { key: 'openai' as const, name: 'OpenAI', icon: Bot, description: 'AI classification & drafts', href: '/integrations' },
                { key: 'shopify' as const, name: 'Shopify', icon: ShoppingBag, description: 'Order & customer lookup', href: '/integrations' },
              ].map((item) => {
                const status = integrations[item.key]
                const isConnected = status === 'connected'
                const isCheckingStatus = status === 'loading'

                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${isConnected ? 'bg-emerald-50' : 'bg-gray-100'}`}>
                        <item.icon className={`w-4 h-4 ${isConnected ? 'text-emerald-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </div>
                    {isCheckingStatus ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
                    ) : isConnected ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300 group-hover:text-amber-400 transition-colors" />
                    )}
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Quick Navigate */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Quick Navigate</h2>
            </div>
            <div className="p-3 space-y-1">
              {[
                { name: 'AI Settings', description: 'Router & priority config', href: '/ai-settings', icon: Zap },
                { name: 'AI Agents', description: 'Configure agent behavior', href: '/agents', icon: Bot },
                { name: 'Knowledge Base', description: 'Manage support docs', href: '/knowledge-base', icon: BookOpen },
              ].map((action) => (
                <Link
                  key={action.name}
                  to={action.href}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <action.icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{action.name}</p>
                      <p className="text-xs text-gray-500">{action.description}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
